<template>
  <div
    class="flex flex-grow items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8"
  >
    <div class="max-w-md w-full space-y-8">
      <div>
        <h2 class="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
          Reset Password
        </h2>
        <p class="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
          Enter your new password below.
        </p>
      </div>
      <form class="mt-8 space-y-6" @submit.prevent="handleResetPassword">
        <div v-if="error" class="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-exclamation-circle text-red-400"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800 dark:text-red-200">
                {{ error }}
              </p>
            </div>
          </div>
        </div>

        <div v-if="success" class="rounded-md bg-green-50 dark:bg-green-900/20 p-4">
          <div class="flex">
            <div class="flex-shrink-0">
              <i class="pi pi-check-circle text-green-400"></i>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-green-800 dark:text-green-200">
                {{ success }}
              </p>
              <p class="mt-2 text-sm text-green-700 dark:text-green-300">
                <router-link to="/login" class="font-medium underline">
                  Click here to log in
                </router-link>
              </p>
            </div>
          </div>
        </div>

        <div>
          <label for="password" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
            New Password
          </label>
          <input
            id="password"
            v-model="form.password"
            name="password"
            type="password"
            autocomplete="new-password"
            required
            minlength="6"
            class="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
            placeholder="Enter new password (min. 6 characters)"
          />
        </div>

        <div>
          <label
            for="confirmPassword"
            class="block text-sm font-medium text-gray-700 dark:text-gray-300"
          >
            Confirm Password
          </label>
          <input
            id="confirmPassword"
            v-model="form.confirmPassword"
            name="confirmPassword"
            type="password"
            autocomplete="new-password"
            required
            minlength="6"
            class="mt-1 appearance-none rounded-md relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white bg-white dark:bg-gray-800 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
            placeholder="Confirm new password"
          />
        </div>

        <div>
          <Button
            type="submit"
            :disabled="loading || success || !isFormValid"
            :label="loading ? 'Resetting...' : 'Reset Password'"
            severity="primary"
            :loading="loading"
            class="w-full"
          />
        </div>

        <div class="text-center">
          <router-link
            to="/login"
            class="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Back to login
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRoute } from "vue-router";
import api from "@/services/api";
import Button from "primevue/button";

export default {
  name: "ResetPassword",
  components: {
    Button
  },
  setup() {
    const route = useRoute();
    const form = ref({
      password: "",
      confirmPassword: ""
    });
    const error = ref("");
    const success = ref("");
    const loading = ref(false);
    const token = ref("");

    // Computed property to check if form is valid
    const isFormValid = computed(() => {
      return (
        token.value &&
        form.value.password.length >= 6 &&
        form.value.confirmPassword.length >= 6 &&
        form.value.password === form.value.confirmPassword
      );
    });

    onMounted(() => {
      // Get token from URL query parameter
      token.value = route.query.token || "";
      if (!token.value) {
        error.value = "Invalid reset token. Please check your email for the correct reset link.";
      }
    });

    const handleResetPassword = async () => {
      try {
        if (!token.value) {
          error.value = "Invalid reset token. Please check your email for the correct reset link.";
          return;
        }

        if (form.value.password !== form.value.confirmPassword) {
          error.value = "Passwords do not match";
          return;
        }

        if (form.value.password.length < 6) {
          error.value = "Password must be at least 6 characters long";
          return;
        }

        loading.value = true;
        error.value = "";
        success.value = "";

        const response = await api.post("/api/auth/reset-password", {
          token: token.value,
          password: form.value.password
        });

        if (response.data.success) {
          success.value = response.data.message || "Password has been reset successfully.";
          // Clear form
          form.value.password = "";
          form.value.confirmPassword = "";
        } else {
          error.value = response.data.message || "Failed to reset password";
        }
      } catch (err) {
        console.error("Reset password error:", err);
        error.value = err.response?.data?.message || "An error occurred. Please try again.";
      } finally {
        loading.value = false;
      }
    };

    return {
      form,
      error,
      success,
      loading,
      token,
      isFormValid,
      handleResetPassword
    };
  }
};
</script>
